'use client'

import { Fragment, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Navbar } from './components/navbar'
import { Footer } from './components/footer'
import { HeroScene } from './components/hero-scene'
import { BookDemoForm } from './components/book-demo-form'
import { Soc2Badge } from './components/soc2-badge'
import { INDUSTRY_LINKS } from './lib/site'
import { capabilityByKey } from './content/capabilities'
import { useCascadeReveal } from './lib/use-cascade-reveal'

/* =========================================
   HERO SECTION
   ========================================= */
function Hero() {
  return (
    <section className="hero">
      <div className="hero-inner">
        {/* LEFT: Content */}
        <div className="hero-left">
          {/* Heading */}
          <h1 className="hero-heading reveal">
            From the first license
            <br />
            to the <em>hundredth.</em>
          </h1>

          {/* Description */}
          <p className="hero-description reveal">
            One system for every application, renewal, and seat. Payna runs
            licensing and compliance for regulated companies and the people
            they license, in every US state and beyond.
          </p>

          {/* CTA */}
          <div className="hero-cta reveal">
            <a href="#book-demo" className="btn-primary btn-primary-lg">
              Book a demo
            </a>
            {/* Keep in step with the seal and /security: see Trust() below. */}
            <Link href="/security" className="hero-compliance">
              <svg viewBox="0 0 16 16" aria-hidden="true" className="hero-compliance-icon">
                <path d="M8 1.5 13.5 3.5v4c0 3.3-2.3 5.9-5.5 7-3.2-1.1-5.5-3.7-5.5-7v-4L8 1.5Z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                <path d="m5.5 8 1.8 1.8L10.7 6.4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              SOC 2 compliant
            </Link>
          </div>
        </div>

        {/* RIGHT: the hero scene. Inline animated SVG, not WebGL and not
            a canvas, despite what this comment used to say. Worth being
            accurate about: it changes whether the thing is crawlable and
            what the performance question even is. */}
        <div className="hero-visual reveal">
          <HeroScene />
        </div>
      </div>
    </section>
  )
}

/* =========================================
   PRODUCTS SPINE
   ========================================= */
const PRODUCTS = [
  {
    key: 'apply',
    href: '/apply',
    eyebrow: '[1] · Get licensed',
    title: 'Apply',
    desc: 'New state license applications for regulated companies: mortgage, insurance entities, lending, debt collection, and money transmitter. From first filing to approval.',
  },
  {
    key: 'seats',
    href: '/seats',
    eyebrow: '[2] · License individuals',
    title: 'Seats',
    desc: 'Per-person licensing, managed at roster scale. MLOs on NMLS: MU4 filings, sponsorships, CE, the renewal window. Adjusters and producers on NIPR: multi-state licensing and deployment readiness.',
  },
  {
    key: 'maintain',
    href: '/maintain',
    eyebrow: '[3] · Stay licensed',
    title: 'Maintain',
    desc: 'Ongoing compliance for every license a company holds: renewals, amendments, call reports, exam preparation, and regulatory change monitoring.',
  },
  {
    key: 'api',
    href: '/api',
    eyebrow: '[4] · Build on license data',
    title: 'API',
    desc: 'Real-time, programmatic access to license status, sponsorship state, CE completion, and regulatory actions. Wire licensure into existing systems.',
  },
]

function Products() {
  return (
    <section className="products-section">
      <div className="products-inner">
        <p className="section-label reveal">THE PLATFORM</p>
        <h2 className="products-heading reveal">
          Four products. One system of record for everything a regulator can
          ask about.
        </h2>
        <div className="products-grid reveal">
          {PRODUCTS.map((p) => (
            <Link
              key={p.href}
              href={p.href}
              className="product-card"
              data-product={p.key}
            >
              <span className="product-eyebrow">{p.eyebrow}</span>
              <h3 className="product-title">{p.title}</h3>
              <p className="product-desc">{p.desc}</p>
              <span className="product-cta">
                Explore {p.title} <span className="btn-arrow">→</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

/* =========================================
   PROOF BAR
   Qualitative proof points, deliberately non-numeric.
   TODO(He Song): replace with real, verifiable figures when we
   have them. The previous band claimed "-90% licensing costs /
   10% of team time / 50+ jurisdictions"; restore numbers only
   if they can be substantiated.
   ========================================= */
const PROOF_POINTS = [
  {
    headline: 'All 50 states',
    label: 'Company licenses and individual seats in every US jurisdiction.',
  },
  {
    headline: 'Nothing files itself',
    label: 'Payna drafts every application and renewal. The compliance team approves before anything goes out.',
  },
  {
    headline: 'Audit-ready, always',
    label: 'A timestamped record of every filing, approval, and change, ready when the examiner asks.',
  },
]

function ProofBar() {
  return (
    <div className="stats-bar">
      <div className="stats-inner">
        {PROOF_POINTS.map((point, i) => (
          <Fragment key={point.headline}>
            {i > 0 && <div className="stat-divider" />}
            <div className="stat-item reveal">
              <span className="stat-number stat-number-text">
                {point.headline}
              </span>
              <p className="stat-label stat-label-wide">{point.label}</p>
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/* =========================================
   PROBLEM SECTION
   Text left; right is an interactive mock of the spreadsheet the
   copy is about. Hovering a row reveals the stamp it deserves. All
   contents are illustrative, obviously so.
   ========================================= */
const SHEET_ROWS = [
  { state: 'CA', item: 'MLO renewals', due: 'DUE 12/31', note: 'v6 or v7?', stamp: 'STALE' },
  { state: 'TX', item: 'Bond rider', due: 'DUE 11/15', note: 'ask legal', stamp: 'MISSED' },
  { state: 'NY', item: 'Call report', due: 'DUE 10/01', note: '', stamp: 'LATE', cursor: true },
  { state: 'FL', item: 'CE hours', due: 'DUE 12/31', note: 'done??', stamp: 'UNKNOWN' },
  { state: 'WA', item: 'Amendment', due: '??', note: 'TODO', stamp: 'UNTRACKED' },
]

function Problem() {
  return (
    <section>
      <div className="problem-section">
        <div className="problem-grid">
          <div>
            <p className="section-label reveal">THE PROBLEM</p>
            <h2 className="problem-heading reveal">
              The most regulated part of the business, managed with
              spreadsheets and prayer.
            </h2>
            <div className="problem-body reveal">
              <p>
                Renewal deadlines buried in spreadsheets no one updates.
                Regulatory changes that surface from outside counsel, weeks
                late.
              </p>
              <p>
                The month before every state exam spent in survival mode. A
                roster of licensed people tracked by hand, one missed renewal
                away from unlicensed activity.
              </p>
              <p>
                Compliance teams are sharp. They&rsquo;re just doing manual
                work that a system should handle for them.
              </p>
            </div>
          </div>
          <div className="sheet reveal reveal-scale" aria-hidden="true">
            <div className="sheet-tab">renewals_FINAL_v7.xlsx</div>
            <div>
              {SHEET_ROWS.map((r) => (
                <div key={`${r.state}-${r.item}`} className="sheet-row">
                  <span className="sheet-state">{r.state}</span>
                  <span className="sheet-item">{r.item}</span>
                  <span className="sheet-due">{r.due}</span>
                  <span className="sheet-note">
                    {r.note}
                    {r.cursor && <span className="sheet-cursor" />}
                  </span>
                  <span className="sheet-stamp">{r.stamp}</span>
                </div>
              ))}
            </div>
            <p className="scene-caption">The current system of record</p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* =========================================
   CAPABILITIES SECTION
   Featured subset; the full ongoing-compliance set lives on
   /maintain.
   ========================================= */
const HOME_CAPABILITIES = [
  {
    num: '[1]',
    ...capabilityByKey('paperwork'),
  },
  {
    num: '[2]',
    title: 'Knows every rule, in every state.',
    desc: 'All 50 US state statutes, NMLS and NIPR procedures, and FinCEN requirements, monitored continuously. When a state changes a bond threshold or a CE requirement, the change surfaces the same day.',
  },
  {
    num: '[3]',
    title: 'Keeps a perfect record of everything.',
    desc: 'Every action is timestamped into a structured, queryable, audit-ready history. When an examiner walks in, the package assembles in seconds.',
  },
]

function Capabilities() {
  return (
    <section className="capabilities-section">
      <div className="capabilities-inner">
        <div className="capabilities-header">
          <p className="section-label reveal">WHAT PAYNA DOES</p>
        </div>
        {HOME_CAPABILITIES.map((cap, i) => (
          <div
            key={cap.num}
            className="capability-item reveal"
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <div className="capability-left">
              <span className="capability-number">{cap.num}</span>
            </div>
            <div>
              <h3 className="capability-title">{cap.title}</h3>
              <p className="capability-desc">{cap.desc}</p>
            </div>
          </div>
        ))}
        <p className="reveal" style={{ marginTop: '2.5rem' }}>
          <Link href="/maintain" className="text-link">
            See everything Maintain covers <span className="btn-arrow">→</span>
          </Link>
        </p>
      </div>
    </section>
  )
}

/* =========================================
   JURISDICTIONS + INDUSTRIES SECTION
   ========================================= */
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
]

/* Industry strip: every entry links to its landing page. Seat
   audiences (MLOs, adjusters, producers) get their own entries
   pointing at the vertical that serves them. */
const INDUSTRY_STRIP = [
  ...INDUSTRY_LINKS,
  { href: '/mortgage', label: 'MLOs' },
  { href: '/insurance', label: 'Insurance Adjusters' },
  { href: '/insurance', label: 'Insurance Producers' },
]

function Jurisdictions() {
  return (
    <section className="jurisdictions-section">
      <div className="jurisdictions-inner">
        <p className="section-label reveal" style={{ textAlign: 'center' }}>
WHERE PAYNA WORKS
        </p>
        <h2 className="jurisdictions-headline reveal">
          50 US states. Companies and their people.
        </h2>
        <p className="jurisdictions-subhead reveal">One system.</p>

        <div className="jurisdiction-grid reveal">
          <div className="jurisdiction-region">
            <span className="jurisdiction-region-label">United States</span>
            <p className="jurisdiction-names">
              {/* The separator is glued to its name inside a nowrap
                  span, so a wrapped line can never start with a
                  stray dot and the left edge stays flush. */}
              {US_STATES.map((name, i) => (
                <Fragment key={name}>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <span className="jurisdiction-name">{name}</span>
                    {i < US_STATES.length - 1 && (
                      <span className="jurisdiction-sep"> ·</span>
                    )}
                  </span>
                  {i < US_STATES.length - 1 && ' '}
                </Fragment>
              ))}
            </p>
          </div>
          <div className="jurisdiction-region">
            <span className="jurisdiction-region-label">Industries</span>
            <p className="jurisdiction-names">
              {INDUSTRY_STRIP.map((entry, i) => (
                <Fragment key={entry.label}>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <Link href={entry.href} className="jurisdiction-name">
                      {entry.label}
                    </Link>
                    {i < INDUSTRY_STRIP.length - 1 && (
                      <span className="jurisdiction-sep"> ·</span>
                    )}
                  </span>
                  {i < INDUSTRY_STRIP.length - 1 && ' '}
                </Fragment>
              ))}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* =========================================
   LATEST NOTES

   A pointer to /blog, not a feed. Two jobs: a reader on the homepage
   should be able to find the writing, and the highest-authority page on
   the site should link into the newest posts, which is the cheapest
   internal linking available.

   Renders nothing when there are no published posts, so the homepage
   never carries an empty "coming soon" band before the first note ships.
   ========================================= */
export type LatestNote = {
  slug: string
  title: string
  date: string
  minutes: number
  topic: string
}

/* =========================================
   TRUST BAND

   The one credibility beat on the page. Sits after Jurisdictions
   (scale) and before Notes, so the product argument runs scale then
   trust rather than being interrupted by it.

   The argument is the seal, not the adjective. "Built with security
   in mind" is what every site says and costs nothing to write. A
   completed third-party audit is a fact somebody else signed. So the
   copy states the audit, gives the reason it would be strange not to
   have one, and leaves the boasting alone.

   Claims are limited to exactly what /security already says: Type I
   and Type II both complete. Change the status on /security, /about,
   the seal, and here together, or the site starts contradicting itself.
   ========================================= */
function Trust() {
  return (
    <section className="trust-band">
      <div className="trust-band-inner">
        <Soc2Badge className="soc2-badge trust-band-seal reveal reveal-scale" />
        <div className="trust-band-copy">
          <p className="section-label reveal reveal-left">SECURITY</p>
          <h2 className="trust-band-heading reveal">
            We are audited, <em>too.</em>
          </h2>
          <p className="trust-band-body reveal">
            Payna holds the filings, credentials, and documents a regulator
            asks about. It would be an odd business that asked you to prove
            your compliance without proving its own. Payna is SOC 2 Type II
            compliant, independently audited over time, not at a single point.
          </p>
          <p className="reveal" style={{ marginTop: '1.75rem' }}>
            <Link href="/security" className="text-link">
              See the trust center <span className="btn-arrow">→</span>
            </Link>
          </p>
        </div>
      </div>
    </section>
  )
}

function Notes({ latest }: { latest: LatestNote[] }) {
  if (latest.length === 0) return null

  return (
    <section className="subpage-section">
      <div className="subpage-inner">
        <p className="section-label reveal">NOTES</p>
        <h2 className="subpage-heading reveal">
          Licensing, as it actually works.
        </h2>
        <ul className="notes-list reveal">
          {latest.map((note) => (
            <li key={note.slug} className="notes-item">
              <Link href={`/blog/${note.slug}`} className="notes-link">
                <span className="notes-meta">
                  {note.topic && (
                    <>
                      {note.topic}
                      <span aria-hidden="true"> · </span>
                    </>
                  )}
                  {note.date} · {note.minutes} min read
                </span>
                <span className="notes-title">{note.title}</span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="reveal" style={{ marginTop: '2.5rem' }}>
          <Link href="/blog" className="text-link">
            All notes <span className="btn-arrow">→</span>
          </Link>
        </p>
      </div>
    </section>
  )
}

/* =========================================
   FOR FIRMS SECTION
   ========================================= */
function ForFirms() {
  return (
    <section className="for-firms-section" id="demo">
      {/* Watermark */}
      <Image
        src="/logo.svg"
        alt=""
        width={167}
        height={171}
        className="for-firms-watermark"
        aria-hidden="true"
      />

      <div className="for-firms-inner">
        <p className="for-firms-label reveal">FOR FIRMS</p>
        <h2 className="for-firms-heading reveal">
          Manage an entire client portfolio from one system.
        </h2>
        <p className="for-firms-body reveal">
          For law firms, compliance consultancies, and outsourced CCOs managing
          a portfolio of regulated clients, Payna plugs directly into the
          practice. Track licensing, renewals, and compliance across every
          client engagement from a single dashboard.
        </p>
        <p className="for-firms-emphasis reveal">
          We build bespoke. Tell us what the practice needs.
        </p>
        <div className="for-firms-actions">
          <a href="#book-demo" className="btn-outline-dark reveal">
            Talk to Us
          </a>
          <Link href="/firms" className="for-firms-more reveal">
            How firms use Payna <span className="btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </section>
  )
}

/* =========================================
   MAIN PAGE
   ========================================= */
export default function HomeClient({ latest = [] }: { latest?: LatestNote[] }) {
  useCascadeReveal()

  return (
    <main>
      <Navbar />
      <Hero />
      <Products />
      <ProofBar />
      <Problem />
      <Capabilities />
      <Jurisdictions />
      <Trust />
      <Notes latest={latest} />
      {/* Hidden for now — keep the section code, just don't render it. */}
      {/* <ForFirms /> */}
      <BookDemoForm />
      <Footer />
    </main>
  )
}
